import React, { useEffect, useMemo, useState } from "react";
import Loader from "@appComponents/Loader/Loader";
import { TReducers } from "@features/reducers";
import { IWalletDetails } from "@market/interfaces/IWallet";
import {
  Col,
  Form,
  Input,
  Row,
  Space,
  Typography,
  Divider,
  Button,
} from "antd";
import { useFormik } from "formik";
import { connect } from "react-redux";
import Big from "big.js";
import * as Yup from "yup";
import SummaryTable, {
  SummaryTableRow,
} from "@appComponents/SummaryTable/SummaryTable";
import CurrencyElement from "@appComponents/CurrencyElement/CurrencyElement";
import FundsService from "@services/Funds.service";
import ReactHtmlParser from "react-html-parser";
import {
  setAccessingFundsAgreementCreatedAt,
  setFundsData,
} from "@funds/features/action.funds";
import ImportantNoticeModal from "@funds/components/ImportantNoticeModal/ImportantNoticeModal";
import CustomSteps from "@funds/components/CustomSteps";
import Section from "../shared/Section/Section";
import PercentageBtns from "../shared/PercentageBtns";
import CustomBtn from "../shared/CustomBtn/CustomBtn";
import Faq from "../shared/Faq";
import unstakingFaq from "./unstakingFaq";
import { Link } from "react-router-dom";
import AlertBox from "@appComponents/AlertBox";
import Modal from "@funds/components/Modal/Modal";

import * as style from "./UnstakeFormStyle.module.scss";
import { appsignal } from "@bundles/appsignal";

type Props = {
  currency: string;
  selectedWallet?: IWalletDetails | undefined;
  history: any;
  setFundsData: (investorId: number) => any;
  companyInvestorId: number | undefined;
  investorId: number;
  accessingFundsAgreementCreatedAt: any;
  setAccessingFundsAgreementCreatedAt: () => void;
  investorSlug: string;
  companySlug: string;
  country?: string;
};

const UnstakeForm = ({
  selectedWallet,
  currency,
  history,
  setFundsData,
  companyInvestorId,
  investorId,
  accessingFundsAgreementCreatedAt,
  setAccessingFundsAgreementCreatedAt,
  investorSlug,
  companySlug,
  country,
}: Props) => {
  const genericInvestorId = companyInvestorId || investorId;

  const [step, setStep] = useState(0);
  const [modalVisible, setMobileVisible] = useState(false);
  const [selectedTag, setSelectedTag] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [balance, setBalance] = useState(0);

  const appLink = companyInvestorId
    ? `/companies/${companySlug}/funds`
    : `/profile/${investorSlug}/funds`;

  useEffect(() => {
    Promise.all([
      setFundsData(genericInvestorId),
      FundsService.getStakingEth(investorId),
    ])
      .then((resp: any) => {
        setIsLoading(false);
        setBalance(resp[1].data.available_staked_eth);
      })
      .catch(() => {
        setIsLoading(true);
      });
  }, []);

  const formik = useFormik({
    initialValues: {
      amount: "",
    },
    validationSchema: Yup.object().shape({
      amount: Yup.string()
        .trim()
        .required("Amount is required")
        .test(
          "validate-min-amount",
          "Please enter an amount greater than or equal to 0.001 ETH",
          (value) => {
            if (!selectedWallet || !value) return true;
            try {
              return !Big(value).lt(0.001);
            } catch {
              return false;
            }
          }
        )
        .test(
          "validate-amount",
          `Provided value is higher than the amount of your staked ETH`,
          (value) => {
            if (!selectedWallet || !value) return true;
            return !Big(value).gt(balance) && !Big(value).eq(0);
          }
        ),
    }),

    onSubmit: async (values) => {
      try {
        await FundsService.unstaking(Big(values.amount).toString());
        history.push(appLink + "/stakingEth");
      } catch (error) {
        appsignal.sendError(error);

        throw new Error("staking failed");
      }
    },
    validateOnMount: false,
  });

  const handleAmountInputChange = (value: any) => {
    if (!selectedWallet) throw new Error("Wallet not selected");
    setSelectedTag(
      Big(value.target.value).div(Big(balance)).times(100).toString()
    );
  };

  const handleAmountPercentage = async (amount: number) => {
    if (!selectedWallet) throw new Error("Wallet not selected");
    const result = Big(
      Big(balance).times(Big(amount).div(Big(100)))
    ).toString();
    await formik.setFieldValue("amount", result, true);
    setSelectedTag(amount.toString());
  };
  if (!selectedWallet || isLoading) return <Loader />;

  if (Big(balance).lte(0)) {
    return (
      <div className={style.wrapper} style={{ marginBottom: "20px" }}>
        <Row gutter={[0, 4]} justify="space-around" align="top">
          <Col sm={24} md={15}>
            <AlertBox
              message="Note!"
              description={
                <>
                  <Typography.Text style={{ fontWeight: 400 }}>
                    We're sorry, it looks like you don't have any Staked
                    Ethereum available to be unstaked.
                  </Typography.Text>
                </>
              }
              type="error"
              showIcon
              style={{ maxWidth: "450px", width: "100%" }}
            />
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className={style.wrapper}>
      <Row gutter={[0, 4]} justify="space-around" align="top">
        <Col
          span={24}
          className={style.header}
          style={{ textAlign: "center", margin: "40px auto" }}
        >
          <Typography.Title> Unstake Ethereum</Typography.Title>
        </Col>
      </Row>

      <Row gutter={[0, 4]} justify="space-around" align="top">
        <Col sm={24} md={15}>
          <Form className={style.headerWrapperSection}>
            <Section>
              <div className={style.headerWrapper}>
                <CustomSteps
                  current={step}
                  labelPlacement="vertical"
                  items={[
                    {
                      title: "Unstaking",
                    },
                    {
                      title: "Order Summary",
                    },
                  ]}
                />
              </div>
              <Divider />
              {step === 0 ? (
                <>
                  <Row>
                    <Col className={style.mainLabel}>
                      <Typography.Title level={5}>Unstake ETH</Typography.Title>
                    </Col>
                  </Row>

                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="amount"
                        style={{ marginBottom: "5px" }}
                        help={
                          formik.touched.amount
                            ? ReactHtmlParser(formik.errors.amount ?? "")
                            : false
                        }
                      >
                        <Space
                          direction="vertical"
                          style={{ width: "100%", marginTop: "20px" }}
                        >
                          <Row>
                            <Col span={8}>
                              <label htmlFor="amount" className={style.label}>
                                Enter amount
                              </label>
                            </Col>
                            <Col span={16} style={{ textAlign: "right" }}>
                              <p style={{ margin: 0 }}>
                                <b>Available balance:</b> {balance}{" "}
                                {currency.toLocaleUpperCase()}
                              </p>
                            </Col>
                          </Row>
                          <div className={style.input}>
                            <Input
                              {...formik.getFieldProps("amount")}
                              type="tel"
                              onInput={handleAmountInputChange}
                              placeholder={`min. 0.001 - max. ${balance}`}
                            />
                          </div>
                        </Space>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row justify="space-between">
                    <Col span={24}>
                      <PercentageBtns
                        handleAmountPercentage={handleAmountPercentage}
                        selectedTag={selectedTag}
                      />
                    </Col>
                  </Row>

                  <Space
                    direction="vertical"
                    style={{ marginTop: "40px", width: "100%" }}
                  >
                    <CustomBtn
                      myType="submit"
                      onClick={() => {
                        setStep(1);
                        window.scrollTo({
                          top: 0,
                          behavior: "smooth",
                        });
                      }}
                      disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                      }
                      className={style.button}
                    >
                      Next
                    </CustomBtn>

                    <CustomBtn
                      myType="cancel"
                      onClick={() => history.push(appLink)}
                      className={style.button}
                    >
                      Cancel
                    </CustomBtn>
                  </Space>
                </>
              ) : (
                <div style={{ marginTop: "50px" }}>
                  <SummaryTable>
                    <SummaryTableRow
                      label={`Unstaking Amount Requested`}
                      value={
                        <CurrencyElement
                          value={formik.values.amount}
                          currency={currency}
                        />
                      }
                    />
                    <SummaryTableRow
                      label={`Remaining Staked ETH balance after unstaking`}
                      value={
                        <CurrencyElement
                          value={Big(balance)
                            .minus(formik.values.amount)
                            .toString()}
                          currency={currency}
                        />
                      }
                    />
                  </SummaryTable>

                  <Space direction="vertical" style={{ width: "100%" }}>
                    <CustomBtn
                      myType="submit"
                      onClick={() => setMobileVisible(true)}
                      loading={formik.isSubmitting}
                      disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                      }
                      className={style.button}
                    >
                      Confirm Order
                    </CustomBtn>

                    <CustomBtn
                      myType="cancel"
                      onClick={() => {
                        setStep(0);
                        window.scrollTo({
                          top: 0,
                          behavior: "smooth",
                        });
                      }}
                      className={style.button}
                    >
                      Cancel
                    </CustomBtn>
                  </Space>
                </div>
              )}
            </Section>
          </Form>
        </Col>
        <Col sm={24} md={7}>
          <Faq faq={unstakingFaq} />
        </Col>
      </Row>
      <Modal
        open={modalVisible}
        centered
        closable
        onCancel={() => setMobileVisible(false)}
        onConfirm={formik.submitForm}
        isLoading={formik.isSubmitting}
        disabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
      >
        <Space direction="vertical">
          <Typography.Title level={4}>Unstake Ethereum</Typography.Title>
          <Typography.Text>
            Are you sure you want to convert back into ETH and stop earning
            rewards?
          </Typography.Text>
        </Space>
      </Modal>
      {!accessingFundsAgreementCreatedAt && (
        <ImportantNoticeModal
          setAccessingFundsAgreementCreatedAt={
            setAccessingFundsAgreementCreatedAt
          }
        />
      )}
    </div>
  );
};

const mapStateToProps = (store: TReducers, ownProps: any) => ({
  investorId: store.userInfo.investor_id,
  companyInvestorId: store.companyReducer.companyDetails.investor_id,
  accessingFundsAgreementCreatedAt:
    store.userInfo.accessing_funds_agreement_created_at,
  selectedWallet: store.fundsReducer.supportedUserWallets?.find(
    (wallet) => wallet.currency === ownProps.currency
  ),
  investorSlug: store.userInfo.nickname,
  country: store.userInfo.country,
  companySlug: store.companyReducer.companyDetails.slug,
});

const mapDispatchToProps = (dispatch: Function) => ({
  setFundsData: (investorId: number) => dispatch(setFundsData(investorId)),
  setAccessingFundsAgreementCreatedAt: () =>
    dispatch(setAccessingFundsAgreementCreatedAt()),
});

export default connect(mapStateToProps, mapDispatchToProps)(UnstakeForm);
